import * as React from 'react';
import { css, html } from 'react-strict-dom';

import { RouterLink, Text } from '../../components';
import { color } from '../../constants/color.css';

type Props = {
  children: React.ReactNode;
};

export default function Prose({ children }: Props) {
  return <html.section style={styles.root}>{children}</html.section>;
}

export function P({ children }: Props) {
  return (
    <Text as="p" style={styles.paragraph}>
      {children}
    </Text>
  );
}

export function H1({ children }: Props) {
  return (
    <Text as="h1" style={styles.h1}>
      {children}
    </Text>
  );
}

export function H2({ children }: Props) {
  return (
    <Text as="h2" style={styles.h2}>
      {children}
    </Text>
  );
}

export function H3({ children }: Props) {
  return (
    <Text as="h3" style={styles.h3}>
      {children}
    </Text>
  );
}

export function Ul({ children }: Props) {
  return <html.ul style={styles.list}>{children}</html.ul>;
}

export function Li({ children }: Props) {
  return (
    <Text as="li" style={styles.listItem}>
      {children}
    </Text>
  );
}

export function Blockquote({ children }: Props) {
  return (
    <Text as="blockquote" style={styles.blockquote}>
      {children}
    </Text>
  );
}

export function Strong({ children }: Props) {
  return (
    <Text as="strong" bold style={styles.strong}>
      {children}
    </Text>
  );
}

export function Em({ children }: Props) {
  return (
    <Text as="em" italic>
      {children}
    </Text>
  );
}

type LinkProps = {
  to: string;
  children: React.ReactNode;
};

// The link styling sits on a wrapping span that the anchor inherits from.
export function ProseLink({ to, children }: LinkProps) {
  return (
    <html.span style={styles.link}>
      <RouterLink to={to}>{children}</RouterLink>
    </html.span>
  );
}

export function ProseAnchor({ id }: { id: string }) {
  return <html.a id={id} />;
}

const styles = css.create({
  root: {
    lineHeight: 1.7,
  },
  h1: {
    fontSize: 32,
    lineHeight: 1.7,
    fontWeight: '600',
    // Browser default heading margin, which RSD zeroes.
    marginBlock: '0.67em',
  },
  h2: {
    fontSize: 27,
    lineHeight: 1.7,
    fontWeight: '500',
    marginTop: 48,
    marginBottom: 24,
  },
  h3: {
    fontSize: 20,
    lineHeight: 1.7,
    fontWeight: '500',
    marginTop: 32,
    marginBottom: 20,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 1.7,
    // No design-system equivalent for the prose text colour.
    color: '#4c4d50',
    marginTop: 20,
    marginBottom: 20,
  },
  list: {
    // RSD removes the default marker and padding.
    listStyleType: 'disc',
    paddingLeft: 26,
    // Browser default list margin, which RSD zeroes.
    marginBlock: '1em',
  },
  listItem: {
    fontSize: 16,
    lineHeight: 1.7,
    color: '#4c4d50',
    marginTop: 8,
    marginBottom: 8,
    paddingLeft: 6,
  },
  blockquote: {
    fontSize: 16,
    lineHeight: 1.7,
    color: '#4c4d50',
    // No design-system equivalent for the blockquote border colour.
    borderLeftWidth: 4,
    borderLeftStyle: 'solid',
    borderLeftColor: '#ccd',
    marginTop: 30,
    marginBottom: 30,
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 15,
  },
  strong: {
    color: color.textDark,
  },
  link: {
    color: color.link,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: '#0366d644',
    ':hover': {
      borderBottomColor: color.link,
    },
  },
});
